import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, CalendarHeart } from "lucide-react";
import Reveal, { EASE } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1693615775129-f2004d6e3e0b?q=80&w=1400&auto=format&fit=crop";

function MaskedLine({ index, children }) {
    return (
        <span className="block overflow-hidden pb-1" aria-hidden="false">
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.25 + index * 0.14 }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ onOpenBooking }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const yFrame = useTransform(scrollYProgress, [0, 1], [0, -50]);

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-cream pt-24 pb-16 lg:pt-28"
        >
            <div
                className="pointer-events-none absolute -left-40 top-1/4 h-[540px] w-[540px] rounded-full bg-coral/20 blur-[140px]"
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute -right-32 -top-24 h-[460px] w-[460px] rounded-full bg-forest/10 blur-[120px]"
                aria-hidden="true"
            />

            <div className="container-x relative grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 xl:gap-16">
                <div className="relative z-10">
                    <motion.span
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                        className="eyebrow mb-5 inline-flex items-center gap-2"
                    >
                        <span className="inline-block h-px w-8 bg-coral" aria-hidden="true" />
                        Pet Shop &amp; Care · para cães &amp; gatos
                    </motion.span>

                    <h1 className="font-serif text-[clamp(2.6rem,7.5vw,4.9rem)] font-bold leading-[1.04] tracking-tight text-forest">
                        <MaskedLine index={0}>O melhor mimo</MaskedLine>
                        <MaskedLine index={1}>para a pata</MaskedLine>
                        <MaskedLine index={2}>
                            <em className="text-coral">mais querida.</em>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
                        className="mt-6 max-w-md text-sm leading-relaxed text-graphite-muted sm:text-base"
                    >
                        Boutique, nutrição super premium e banho &amp; tosa para cães e gatos que são
                        família — um pet shop pensado nos mínimos detalhes, do focinho à ponta do rabo.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <button
                            type="button"
                            data-testid="hero-explore-button"
                            onClick={() => scrollToId("catalogo")}
                            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-forest px-7 py-4 text-sm font-bold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-light hover:shadow-[0_18px_36px_rgba(23,61,53,0.3)]"
                        >
                            Explorar o catálogo
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                        </button>
                        <button
                            type="button"
                            data-testid="hero-schedule-button"
                            onClick={onOpenBooking}
                            className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-forest/15 bg-cream-card/60 px-7 py-[14px] text-sm font-bold text-forest backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-coral hover:text-coral-dark"
                        >
                            <CalendarHeart className="h-4 w-4" />
                            Agendar banho &amp; tosa
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.15 }}
                        className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-bold uppercase tracking-[0.22em] text-forest/50"
                    >
                        <span>Banho &amp; Tosa</span>
                        <span className="h-1 w-1 rounded-full bg-coral" aria-hidden="true" />
                        <span>Nutrição Premium</span>
                        <span className="h-1 w-1 rounded-full bg-coral" aria-hidden="true" />
                        <span>Brinquedos</span>
                    </motion.div>
                </div>

                <div className="relative mx-auto w-full max-w-[440px] lg:max-w-none">
                    <motion.div
                        style={{ y: yFrame }}
                        className="absolute -right-3 -top-4 h-full w-full rounded-[2.5rem] bg-coral/60 sm:-right-5 sm:-top-6"
                        aria-hidden="true"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 1.2, ease: EASE, delay: 0.35 }}
                        className="relative overflow-hidden rounded-[2.5rem] border border-forest/10 shadow-[0_40px_80px_rgba(23,61,53,0.22)]"
                    >
                        <motion.img
                            style={{ y: yImg, scale: 1.12 }}
                            src={HERO_IMG}
                            alt="Golden retriever sorrindo, retrato editorial"
                            className="aspect-[4/5] w-full object-cover"
                            loading="eager"
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-t from-forest/25 via-transparent to-transparent"
                            aria-hidden="true"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, rotate: -30, scale: 0.6 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        transition={{ duration: 1, ease: EASE, delay: 1 }}
                        className="absolute -left-6 -top-8 hidden h-28 w-28 sm:block"
                        aria-hidden="true"
                    >
                        <div className="relative grid h-full w-full place-items-center">
                            <svg viewBox="0 0 100 100" className="absolute inset-0 animate-spin-slow">
                                <defs>
                                    <path id="badge-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                                </defs>
                                <text className="fill-forest text-[9.5px] font-bold uppercase tracking-[0.24em]">
                                    <textPath href="#badge-circle">
                                        Mimo &amp; Pata · Pet Shop &amp; Care ·
                                    </textPath>
                                </text>
                            </svg>
                            <span className="grid h-11 w-11 place-items-center rounded-full bg-coral font-serif text-lg font-bold text-forest">
                                M
                            </span>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
                        className="absolute -bottom-6 -left-4 sm:-left-10"
                    >
                        <div className="animate-float rounded-3xl border border-sand bg-cream-card/90 px-5 py-4 shadow-[0_24px_48px_rgba(23,61,53,0.16)] backdrop-blur">
                            <p className="flex items-center gap-2 text-sm font-bold text-forest">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-coral/20">
                                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-coral-dark" aria-hidden="true">
                                        <path d="M12 21s-7.5-4.7-9.7-9A5.6 5.6 0 0 1 12 6.4 5.6 5.6 0 0 1 21.7 12c-2.2 4.3-9.7 9-9.7 9z" />
                                    </svg>
                                </span>
                                Feito para pets exigentes
                            </p>
                            <p className="mt-1 pl-10 text-[11px] font-semibold uppercase tracking-[0.18em] text-graphite-muted">
                                Experiência demonstrativa
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <Reveal delay={1.4} y={0} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block">
                <div className="flex flex-col items-center gap-2 text-forest/40">
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Role</span>
                    <span className="h-10 w-px animate-pulse bg-forest/30" aria-hidden="true" />
                </div>
            </Reveal>
        </section>
    );
}
